export interface SentryEventLike {
  request?: {
    headers?: Record<string, string>;
  };
  tags?: Record<string, string>;
  user?: {
    id?: string;
    username?: string;
    email?: string;
    ip_address?: string;
    [key: string]: unknown;
  };
  level?: string;
}

/**
 * Canonical lowercase set of auth-bearing header names that must never reach
 * Sentry. HTTP header names are case-insensitive per RFC 7230 §3.2, and the
 * casing observed in `event.request.headers` depends on the runtime that
 * produced it (Node's `http` lowercases everything, browsers vary, the Sentry
 * SDKs pass headers through as observed). Matching is therefore done on the
 * lowercased key so a leaked `authorization` cannot slip past a check for
 * `Authorization`.
 */
const AUTH_HEADER_NAMES_LOWERCASE: ReadonlySet<string> = new Set([
  'authorization',
  'cookie',
  'x-api-key',
]);

const SENSITIVE_TAG_KEYS = ['password', 'token', 'secret', 'apiKey', 'api_key'] as const;

/**
 * Remove any auth-bearing headers from a headers map regardless of key
 * casing. Mutates the passed-in object. Exported so breadcrumb scrubbers in
 * each surface's Sentry hooks can share the same case-insensitive contract
 * instead of re-implementing case-sensitive deletes.
 */
export function scrubAuthHeaders(headers: Record<string, unknown>): void {
  for (const key of Object.keys(headers)) {
    if (AUTH_HEADER_NAMES_LOWERCASE.has(key.toLowerCase())) {
      delete headers[key];
    }
  }
}

/**
 * Strip auth headers, sensitive tags, and PII from a Sentry event.
 * Mutates the event in place for performance, then returns it.
 * Shared across all TS surfaces per OBSERVABILITY_SPEC §5.10.
 */
export function scrubPii<T extends SentryEventLike>(event: T): T {
  if (event.request?.headers) {
    scrubAuthHeaders(event.request.headers as Record<string, unknown>);
  }

  if (event.tags) {
    for (const key of SENSITIVE_TAG_KEYS) {
      delete event.tags[key];
    }
  }

  if (event.user) {
    delete event.user.email;
    event.user.ip_address = '{{auto}}';
  }

  return event;
}
